import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, switchMap, take } from 'rxjs';

import { env } from '@amfam/environment';
import {
  selectPolicyPartnerIdFromSummary,
  selectSourceSystem
} from '@amfam/policy/data-access/lib/+state/summaries/summaries.selectors';
import { selectAddress, selectEmailAddress } from '@amfam/shared/user/lib/+state/user.selectors';

import { selectUserPreferencesByPolicy } from '../+state/preferences.selectors';
import {
  RetrievePreferencFromExpAPIResponse,
  UpdatePreferenceFromExpAPIRequest
} from '../models/user-account-preferences.model';

@Injectable({
  providedIn: 'root'
})
export class UserAccountPreferencesService {
  private http = inject(HttpClient);
  private store = inject(Store);

  /**
   * Get preferences from user experience API
   * @param policyNumber - The policy number to retrieve preferences for
   * @returns Observable of preferences response
   */
  public getPreferencesFromExpApi(
    policyNumber: string
  ): Observable<RetrievePreferencFromExpAPIResponse> {
    // Policy preferences call requires afe-partner-id
    return this.store.select(selectPolicyPartnerIdFromSummary(policyNumber)).pipe(
      take(1),
      switchMap(partnerID => {
        const headers = new HttpHeaders().set('afe-partner-id', partnerID ?? '');
        const preferencesExpApiPath = `${env.userAccountApi}/policies/${policyNumber}/preferences`;
        return this.http.get<RetrievePreferencFromExpAPIResponse>(preferencesExpApiPath, {
          headers: headers
        });
      })
    );
  }

  /**
   * Update user account preferences via Experience API
   * @param policyId - The policy ID to update preferences for
   * @param preferenceOption - The preference option ('EMAIL' or 'PAPER')
   * @returns Observable of the HTTP response
   */
  public updateUserAccountPreferencesFromExpApi(
    policyId: string,
    preferenceOption: string
  ): Observable<unknown> {
    return combineLatest([
      this.store.select(selectPolicyPartnerIdFromSummary(policyId)),
      this.store.select(selectEmailAddress),
      this.store.select(selectSourceSystem(policyId)),
      this.store.select(selectUserPreferencesByPolicy(policyId)),
      this.store.select(selectAddress)
    ]).pipe(
      take(1),
      switchMap(([partnerID, emailAddress, sourceSystem, preferences, address]) => {
        const headers = new HttpHeaders().set('afe-partner-id', partnerID ?? '');

        // Format address as a string for PAPER delivery destination
        const formattedAddress = address
          ? `${address.line1}${address.line2 ? ', ' + address.line2 : ''}, ${address.city}, ${address.state} ${address.zip5}`
          : '';

        const requestPayload: UpdatePreferenceFromExpAPIRequest = {
          policyId: policyId,
          preference: {
            preferenceSource: {
              sourceSystemName: sourceSystem ?? '',
              sourceUserId: 'Myaccount' // afe-source-id from user state
            },
            businessContext: preferences?.businessContext ?? '',
            preferenceLabel: preferences?.preferenceLabel ?? '',
            preferenceOption: preferenceOption.toUpperCase(), // 'EMAIL' or 'PAPER'
            deliveryDestination:
              preferenceOption.toUpperCase() === 'PAPER' ? formattedAddress : (emailAddress ?? '')
          }
        };

        const preferencesExpApiPath = `${env.userAccountApi}/policies/${policyId}/preferences`;

        return this.http.put(preferencesExpApiPath, requestPayload, {
          headers: headers
        });
      })
    );
  }
}
